import { createAsyncThunk } from "@reduxjs/toolkit";
import { apiConnector } from "../apiConnector";
import { endpoint } from "../apis";
import Cookies from "js-cookie";
import { toast } from "react-toastify";

// Async Thunk for Campaign Handler
export const campaignHandler = createAsyncThunk(
  "campaignHandler/action",
  async ({ actionType, data, showToast = true }, { rejectWithValue }) => {
    try {
      const token = Cookies.get("authToken");
      let payload;

      console.log(`Campaign ${actionType} action with data:`, data);

      switch (actionType) {
        case "list":
          payload = {
            action: "list",
            company_id: data.company_id,
          };
          const response = await apiConnector(
            "post",
            endpoint.CAMPAIGN_HANDLER,
            payload,
            { Authorization: `Bearer ${token}` }
          );
          
          console.log('Campaign list API response:', response.data);
          
          if (response.data.Response) {
            // Check the structure of the response
            const campaigns = response.data.Response.campaigns || [];
            console.log('Campaigns extracted from response:', campaigns);
            
            if (showToast) {
              toast.success("Campaigns fetched successfully!");
            }
            return { actionType, data: campaigns };
          } else {
            return rejectWithValue("Unexpected response format.");
          }

        case "get":
          console.log('Campaign get action with campaign_id:', data.campaign_id);
          payload = {
            action: "get",
            campaign_id: data.campaign_id,
          };
          const getCampaignResponse = await apiConnector(
            "post",
            endpoint.CAMPAIGN_HANDLER,
            payload,
            { Authorization: `Bearer ${token}` }
          );
          console.log('Campaign get response:', getCampaignResponse.data);
          if (getCampaignResponse.data.Response) {
            if (showToast) {
              toast.success("Campaign details fetched successfully!");
            }
            // Ensure we return the campaign data in a consistent format
            const campaignData = getCampaignResponse.data.Response;
            return { actionType, data: campaignData };
          } else {
            return rejectWithValue("Unexpected response format.");
          }

        case "create":
          // For create action, we need to set status to Draft
          payload = {
            action: "create",
            status: "Draft",
            ...data,
          };
          break;

        case "update":
          // Map to update_content action for API
          payload = {
            action: "update",
            campaign_id: data.campaign_id,
            ...data,
          };
          break;

        case "publish":
          payload = {
            action: "publish",
            campaign_id: data.campaign_id,
          };
          break;

        case "preview_budget":
          // For preview_budget action, send all campaign data but with preview_budget action
          payload = {
            action: "preview_budget",
            ...data,
          };
          break;

        default:
          return rejectWithValue("Invalid action type");
      }

      console.log(`Sending ${actionType} request with payload:`, payload);
      
      const campaignResponse = await apiConnector(
        "post",
        endpoint.CAMPAIGN_HANDLER,
        payload,
        { Authorization: `Bearer ${token}` }
      );

      console.log(`${actionType} response:`, campaignResponse.data);

      if (campaignResponse.data.Response) {
        switch (actionType) {
          case "create":
            if (showToast) {
              toast.success(`Campaign ${data.name || 'draft'} created successfully!`);
            }
            // Return the complete response including campaign_data if available
            return { 
              actionType, 
              data: campaignResponse.data.Response,
              campaign_data: campaignResponse.data.campaign_data || null
            };

          case "update":
            if (showToast) {
              toast.success(`Campaign ${data.name || ''} updated successfully!`);
            }
            // Return the complete response including campaign_data if available
            return { 
              actionType, 
              data: campaignResponse.data.Response,
              campaign_data: campaignResponse.data.campaign_data || null
            };
            
          case "publish":
            if (showToast) {
              toast.success("Campaign published successfully!");
            }
            return { actionType, data: campaignResponse.data.Response };

          case "preview_budget":
            // For preview_budget, return the response with budget data
            // Don't show toast as it's handled in the component
            return { 
              actionType, 
              data: campaignResponse.data.Response,
              budget: campaignResponse.data.Response?.budget || campaignResponse.data.budget || 0,
              campaign_data: campaignResponse.data.campaign_data || null
            };

          default:
            return rejectWithValue("Invalid action type");
        }
      } else {
        return rejectWithValue("Unexpected response format.");
      }
    } catch (error) {
      console.error(`Error in ${actionType} action:`, error);
      if (error?.response?.data === "('Token has been expired',)") {
        localStorage.clear();
        const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'http://www.katalisia.com';
        window.location.href = frontendUrl;
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(error.response?.data || "API request failed.");
      }
      return rejectWithValue(error.response?.data || "API request failed.");
    }
  }
);

// Async Thunk for Coupon Application
export const applyCoupon = createAsyncThunk(
  "applyCoupon/action",
  async ({ data, showToast = true }, { rejectWithValue }) => {
    try {
      const token = Cookies.get("authToken");
      
      // Prepare payload for coupon application
      const payload = {
        action: "apply_coupon",
        campaign_id: data.campaign_id,
        coupon_code: data.coupon_code,
      };
      
      console.log('Applying coupon with payload:', payload);
      
      const couponResponse = await apiConnector(
        "post",
        endpoint.APPLY_COUPON,
        payload,
        { Authorization: `Bearer ${token}` }
      );
      
      console.log('Coupon response:', couponResponse.data);
      
      if (couponResponse.data.Response) {
        // Handle new API response format: { Response: { success: true, message: "...", discount_percent: "10" } }
        const response = couponResponse.data.Response;
        
        if (response.success === true) {
          if (showToast) {
            toast.success(response.message || "Coupon applied successfully!");
          }
          
          return { 
            success: true,
            discount_percent: parseInt(response.discount_percent) || 0,
            message: response.message || "Coupon applied successfully!"
          };
        } else {
          // Handle error response with success: false
          const errorMessage = response.message || "Failed to apply coupon";
          if (showToast) {
            toast.error(errorMessage);
          }
          return rejectWithValue(errorMessage);
        }
      } else {
        return rejectWithValue("Unexpected response format.");
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      if (error?.response?.data === "('Token has been expired',)") {
        localStorage.clear();
        const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'http://www.katalisia.com';
        window.location.href = frontendUrl;
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(error.response?.data || "Failed to apply coupon.");
      }
      return rejectWithValue(error.response?.data || "Failed to apply coupon.");
    }
  }
);

// Async Thunk for Coupon Removal
export const removeCoupon = createAsyncThunk(
  "removeCoupon/action",
  async ({ data, showToast = true }, { rejectWithValue }) => {
    try {
      const token = Cookies.get("authToken");
      
      // Prepare payload for coupon removal
      const payload = {
        action: "remove_coupon",
        campaign_id: data.campaign_id,
      };
      
      console.log('Removing coupon with payload:', payload);
      
      const couponResponse = await apiConnector(
        "post",
        endpoint.APPLY_COUPON,
        payload,
        { Authorization: `Bearer ${token}` }
      );
      
      console.log('Remove coupon response:', couponResponse.data);
      
      if (couponResponse.data.Response) {
        // Handle API response format
        const response = couponResponse.data.Response;
        
        if (response.success === true) {
          if (showToast) {
            toast.success(response.message || "Coupon removed successfully!");
          }
          
          return { 
            success: true,
            message: response.message || "Coupon removed successfully!"
          };
        } else {
          // Handle error response with success: false
          const errorMessage = response.message || "Failed to remove coupon";
          if (showToast) {
            toast.error(errorMessage);
          }
          return rejectWithValue(errorMessage);
        }
      } else {
        return rejectWithValue("Unexpected response format.");
      }
    } catch (error) {
      console.error("Error removing coupon:", error);
      if (error?.response?.data === "('Token has been expired',)") {
        localStorage.clear();
        const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'http://www.katalisia.com';
        window.location.href = frontendUrl;
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(error.response?.data || "Failed to remove coupon.");
      }
      return rejectWithValue(error.response?.data || "Failed to remove coupon.");
    }
  }
);